import { createSelector } from 'reselect';
import { getBrandCode, isPropStream } from 'utils/brand';
import { List } from 'immutable';
import { ReportTypes, Reports, Permissions, planTypes } from './constants';


const emptyList = List();

const checkState = state => state;

export const selectUser = createSelector(
  checkState,
  state => state.get('user'),
);

export const selectProfile = createSelector(
  selectUser,
  user => user.get('profile'),
);

export const getMarketingUrl = (profile, type) => profile.get(`${type.name.toLowerCase()}Url`);

export const selectPermissions = createSelector(
  selectUser,
  user => user.get('permissions'),
);

export const selectFullAccessPermission = createSelector(
  selectPermissions,
  permissions => !!(permissions && permissions.includes(Permissions.searchFull)),
);

export const selectPages = createSelector(
  selectUser,
  user => user.get('pages', emptyList),
);

export const selectReports = createSelector(
  selectUser,
  user => user.get('reports', emptyList).sort((a, b) => a.get('seq') - b.get('seq')),
);

export const selectAlerts = createSelector(
  selectUser,
  user => user.get('alerts', emptyList),
);

export const selectPremiumReports = createSelector(
  selectReports,
  reports => reports.filter(report => report.get('type') === ReportTypes.premium),
);

export const selectPropertyReports = createSelector(
  selectReports,
  reports => reports.filter(report => (report.get('type') === ReportTypes.property || report.get('type') === ReportTypes.analysis) && report.get('code') !== Reports.comparativeMarketAnalysis),
);

export const selectAnalysisReports = createSelector(
  selectReports,
  reports => reports.filter(report => report.get('type') === ReportTypes.analysis),
);

export const selectIsAuthed = createSelector(
  selectUser,
  user => user.get('isAuthed'),
);

export const selectIsLoading = createSelector(
  selectUser,
  user => user.get('isLoading'),
);

export const selectLoading = createSelector(
  selectUser,
  user => user.get('loading'),
);

export const selectAlert = createSelector(
  selectUser,
  user => user.get('alert'),
);

export const selectAlertLoading = createSelector(
  selectUser,
  user => user.get('alertLoading'),
);

export const selectOffers = createSelector(
  selectUser,
  user => user.get('offers'),
);

export const selectOfferLoading = createSelector(
  selectUser,
  user => user.get('offerLoading'),
);

export const selectUsers = createSelector(
  selectUser,
  user => user.get('users'),
);

export const selectTransactions = createSelector(
  selectUser,
  user => user.get('transactions'),
);

export const selectLayouts = createSelector(
  selectUser,
  user => user.get('layouts'),
);

export const selectDuplicateSession = createSelector(
  selectUser,
  user => user.get('duplicateSession'),
);

export const selectLoggedOut = createSelector(
  selectUser,
  user => user.get('loggedOut'),
);

export const selectChatVisible = createSelector(
  selectUser,
  user => user.get('chatVisible'),
);

export const selectLogoutMessage = createSelector(
  selectUser,
  user => user.get('logoutMessage'),
);

export const selectPropertyGridLayout = createSelector(
  selectUser,
  user => {
    return user.get('pages').find(p => p.get('type') === 'GRID' && p.get('code') === 'SAVED_PROPERTY')
  },
);

export const selectPropertyDetailLayouts = createSelector(
  selectUser,
  user => user.get('pages').filter(p => p.get('type') === 'PROPERTY'),
);

export const selectCardCodeRequired = createSelector(
  selectUser,
  user => user.get('cardCodeRequired'),
);


export const selectPabEnabled = createSelector(
  selectProfile,
  profile => profile.get('pabEnabled'),
);

// The Phase-1 Investor Outreach rollout flag (ATAI-110 / S4) is retired in S7
// (ATAI-260): Phase 2 ships without a feature toggle, so entitlement and the §2
// visibility matrix are the only gates. The sidebar and the feature route now
// read selectInvestorOutreachNavVisible (below) instead of this flag.
// (The leftover `investorOutreachEnabled` reducer default is harmless; remove it
// in coordination with the Phase-1 owner.)

export const selectUserId = createSelector(
  selectProfile,
  profile => profile.get("id"),
);

export const selectPrimaryUserId = createSelector(
  selectUser,
  user => user.get("primaryUserId"),
);

export const selectCustomerNumber = createSelector(
  selectUser,
  user => user.get("customerNumber"),
);

// marketing Profile
export const selectMarketingProfile = createSelector(
  selectUser,
  user => user.get('marketingProfile'),
);
export const selectUserProfile = createSelector(
  selectUser,
  user => user.get('userProfile'),
);
export const selectPrepaidCredits = createSelector(
  selectUser,
  user => user.get('prepaidCredits'),
);

export const selectPlanDetails = createSelector(
  selectUser,
  user => user.get('planDetails'),
);
export const selectLAPlanDetails = createSelector(
  selectUser,
  user => user.get('laPlanDetails'),
);
export const selectLeadAutomator = createSelector(
  selectUser,
  user => user.get('leadAutomator'),
);

export const selectIsLeadAutomatorLoading = createSelector(
  selectUser,
  user => user.get('isLeadAutomatorLoading') ?? false,
);

export const selectIsLeadAutomatorLoaded = createSelector(
  selectUser,
  user => user.get('isLeadAutomatorLoaded') ?? false,
);

export const selectTeams = createSelector(
  selectUser,
  user => user.get('teams'),
);

export const selectTeamMembers = createSelector(
  selectTeams,
  teams => teams.get('teamMembers').toJS(),
);

export const selectTeamMembersLoading = createSelector(
  selectTeams,
  teams => teams.get('loading'),
);

export const selectSelectedTeamMember = createSelector(
  selectTeams,
  teams => teams.get('selectedTeamMember'),
);

export const selectUserPlan = createSelector(
  selectPlanDetails, // This should be the Immutable.js version
  (planDetails) => {
    const details = planDetails?.toJS?.(); // Convert from Immutable if needed
    if (
      details?.accountEnrolledPlans &&
      details?.accountEnrolledPlans?.length > 0
    ) {
      const selectedPlan = details?.availableUserPlans.find(
        plan => plan?.planAmount === details?.accountEnrolledPlans[0]?.amount
      );

      const planName = selectedPlan?.name || "";

      if (planName?.includes("Essentials")) return "Essentials";
      else if (planName?.includes("Pro")) return "Pro";
      else if (planName?.includes("Elite")) return "Elite";
      else return "Legacy";
    }

    return "";
  }
);

// Promote PropStream Connect (PROPS-3091): only show the upsell to users who don't
// already have it, either as a standalone add-on (accountPlanMrcs) or bundled into
// their Pro/Elite plan. Fails safe: until accountPlanMrcs has actually loaded, we
// can't tell whether the user already owns the add-on, so default to NOT promoting
// rather than risk upselling someone who already paid for it.
export const selectShouldPromotePropStreamConnect = createSelector(
  selectLeadAutomator,
  selectIsLeadAutomatorLoaded,
  selectUserPlan,
  (leadAutomator, isLeadAutomatorLoaded, userPlan) => {
    const isBundledPlan = userPlan === "Pro" || userPlan === "Elite";
    if (isBundledPlan) return false;
    if (!isLeadAutomatorLoaded) return false;

    const la = leadAutomator?.toJS ? leadAutomator.toJS() : leadAutomator;
    const hasPropStreamConnect = Boolean(la?.accountPlanMrcs?.length);

    return !hasPropStreamConnect;
  }
);

export const selectTeamCounts = createSelector(
  selectTeams,
  teams => teams.get('teamCounts').toJS(),
);

// Selector to check if full access creation is allowed
export const selectCanCreateFullAccess = createSelector(
  selectUserPlan,  // Assuming this selector returns the user's current plan (e.g., 'Pro', 'Elite')
  selectTeamCounts,
  (userPlan, counts) => {
    const plan = planTypes.find(plan => plan.id === userPlan);
    if (!plan) return false;

    return counts.fullAccessCount < plan.maxfull;
  }
);

export const selectShowFinishBuildingYourTeam = createSelector(
  selectUserPlan,
  selectTeamCounts,
  (userPlan, counts) => {
    const plan = planTypes.find(plan => plan.id === userPlan);
    return plan ? counts.fullAccessCount < plan.preLoaded : false;
  }
);


// this need to move
export const selectPaidFullAccess = createSelector(
  selectUserPlan,
  selectTeamCounts,
  (userPlan, counts) => {
    const plan = planTypes.find(plan => plan.id === userPlan);
    if (!plan) return { canCreate: false, isPaidUser: false };

    if (counts.fullAccessCount >= plan.maxfull) {
      return { canCreate: false, isPaidUser: false }; // Max limit reached, cannot create
    }

    if (counts.fullAccessCount >= plan.preLoaded) {
      return { canCreate: true, isPaidUser: true }; // Paid user warning
    }

    return { canCreate: true, isPaidUser: false }; // Free user, within limits
  }
);


export const selectIsPaidUser = createSelector(
  selectTeams,
  teams => teams?.get('isPaidUser')
);

export const selectCanCreate = createSelector(
  selectTeams,
  teams => teams?.get('canCreate')
);


export const selecFullAccount = createSelector(
  selectProfile,
  // profile => false
  profile => ["TEAM", "TEAM_FULL"].includes(profile.get("type"))
);

// Who may see Investor Outreach surfaces beyond the account card (e.g. the Contacts Investor List).
// Primary holder: needs a live subscription. Team member: needs the permission (S10) on a subscribed
// account. Scouts and white-label brands never. Both flags come from the profile (propstream-be).
export const selectCanAccessInvestorOutreach = createSelector(
  selectProfile,
  profile => {
    if (getBrandCode() !== 'propstream') return false;
    if (profile.get('subUser') && profile.get('scoutUser')) return false;
    // A backend that does not send the flag yet (propstream-be not deployed with ATAI-275) keeps the
    // previous behaviour (visible) instead of hiding the Investor List from everyone.
    if (!profile.has('investorOutreachSubscribed')) return true;
    if (!profile.get('investorOutreachSubscribed')) return false;
    const isTeamMember = ["TEAM", "TEAM_FULL"].includes(profile.get("type"));
    return isTeamMember ? Boolean(profile.get('allowInvestorOutreach')) : true;
  }
);

// Does this user see the Investor Outreach nav item / Account card? (ATAI-260 · S7)
// The §2 visibility matrix, shared by the sidebar, the feature route and the S6 card so the
// three surfaces can't drift. Primary holder: always — unsubscribed included, since it is their
// only route to buy. Team member: the per-member permission (S10) on a subscribed account. Never
// Scouts or white-label. Mirrors AccountNew/Sections/InvestorOutreach.js `shouldRender`.
export const selectInvestorOutreachNavVisible = createSelector(
  selectProfile,
  profile => {
    if (!isPropStream()) return false; // never on white-label
    if (profile.get('subUser') && profile.get('scoutUser')) return false; // Scout
    const isTeamMember = ["TEAM", "TEAM_FULL"].includes(profile.get("type"));
    if (isTeamMember) {
      return Boolean(profile.get('investorOutreachSubscribed')) && Boolean(profile.get('allowInvestorOutreach'));
    }
    return true; // primary account holder
  }
);

// billing

export const selectBillingState = createSelector(
  selectUser,
  user => user.get('billing'),
);

export const selectBillingDashboard = createSelector(
  selectBillingState,
  user => user.get('dashboard').toJS(),
);


export const selectWalletList = (state) => selectBillingState(state).get('wallet').toJS()

export const selectWalletIsLoading = createSelector(
  selectBillingState,
  billing => billing.get('isLoading'),
);

export const selectCardList = createSelector(
  selectBillingState,
  billing => billing.get('cardList').toJS(),
);

export const selectDefaultPaymentSetting = createSelector(
  selectBillingState,
  billing => billing.get('defaultPaymentSettingList').toJS(),
);

export const selectBillingHistory = createSelector(
  selectBillingState,
  billing => billing.get('billingHistory').toJS(),
);

export const selectPaymentMethodById = (state, paymentMethodId) =>
  selectWallet(state).find((method) => method.get('id') === paymentMethodId);


export const selectUsageSection = createSelector(
  selectUser,
  user => user.get('usage').toJS(),
);

export const selectPlanMrcByPin = (plan) => createSelector(
  selectProfile,
  profile => {
    const scoutRecords = profile?.get('planMrcs')?.toJS().filter(item => item?.userType === plan);
    return scoutRecords && scoutRecords.length ? scoutRecords[0] : null
  }
);


export const selectSelectedTeamMemberplantypesList = createSelector(
  selectTeams,
  teams => teams.get('plantypes').toJS(),
);

export const selectVendorPhoneNumber = createSelector(
  selectTeams,
  teams => teams.get('vendorPhoneNumber'),
);

export const selectVendorEmailAddress = createSelector(
  selectTeams,
  teams => teams.get('vendorEmailAddress'),
);

export const selectNextBillingCycleDate = createSelector(
  selectTeams,
  teams => teams.get('tmNextBillingCycleDate'),
);

export const selectRemainingCycleDays = createSelector(
  selectTeams,
  teams => teams.get('remainingCycleDays'),
);

export const selectPlanTypesByUserType = (userType, isPaidUser) => createSelector(
  selectSelectedTeamMemberplantypesList,
  (planTypes) => {
    const trimmedUserType = userType?.trim();
    const filteredPlans = planTypes?.filter(plan => {
      const matchesUserType = plan?.userType === trimmedUserType;
      if (!matchesUserType) return false;
      if (trimmedUserType === "TEAM_FULL") {
        return isPaidUser ? plan?.amount === 30.00 : plan?.amount === 0.00;
      }
      // Default logic for other user types
      return true;
    });
    return filteredPlans?.length ? filteredPlans[0] : null;
  }
);

export const isLegacyAccountPlan = createSelector(
  selectPlanDetails,
  (planDetails) => {
    if (!planDetails || typeof planDetails.toJS !== 'function') {
      return false;
    }

    const details = planDetails.toJS();
    const plans = details?.accountEnrolledPlans;

    if (!Array.isArray(plans) || plans.length === 0) {
      return false;
    }

    const planId = plans[0]?.planId;
    return planId < 943;
  }
);
export const selectListManagementEnabled = createSelector(
  [selectPlanDetails, selectProfile],
  (planDetails, profile) => {
    if (planDetails && typeof planDetails.toJS === 'function') {
      const planValue = planDetails.toJS()?.listManagementEnabled;
      if (planValue !== undefined) {
        return planValue;
      }
    }
    if (profile) {
      return profile.get('listManagementEnabled');
    }
    return false;
  }
);

export const selectCallCountForToday = (state) =>
  state.getIn(['user', 'integrations', 'callCountForToday']);

export const selectCallCountForTodayLoading = (state) =>
  state.getIn(['user', 'integrations', 'callCountForTodayLoading']);

export const selectBatchDialerList = createSelector(
  selectUser,
  (user) => {
    const list = user.getIn(['integrations', 'batchDialer']);
    return list ? list.toJS() : [];
  }
);

export const selectCallSettings = createSelector(
  selectUser,
  user => user.getIn(["integrations", "callSettings"])?.toJS() ?? []
);

export const selectCallSettingsLoading = createSelector(
  selectUser,
  user => user.getIn(['integrations', 'callSettingsLoading']) ?? false
);

export const selectCallSettingsLoaded = createSelector(
  selectUser,
  user => user.getIn(['integrations', 'callSettingsLoaded']) ?? false
);

export const selectLoadingCallSettingsAvailableOutboundNumbers = createSelector(
  selectUser,
  user => user.getIn(["integrations", "loadingAvailableOutboundNumbers"]) ?? null
);

export const selectCallSettingsAvailableOutboundNumbers = createSelector(
  selectUser,
  user => user.getIn(["integrations", "availableOutboundNumbers"])?.toJS() ?? null
);

export const selectStateAreaCodes = createSelector(
  selectUser,
  user => user.getIn(['integrations', 'stateAreaCodes'])?.toJS() ?? []
);

export const selectCampaignsList = createSelector(
  selectUser,
  (user) => {
    const list = user.getIn(['integrations', 'campaigns']);
    return list?.toJS()?.batchDialerCampaigns || [];  // return only array
  }
);

export const selectBatchDialerSource = createSelector(
  selectUser,
  user => user.get('batchDialerSource')
);
export const selectIsClickToDialOpen = createSelector(
  selectUser,
  user => user.getIn(['integrations', 'isClickToDialOpen']) ?? false
);

export const selectIsClickToDialLoaded = createSelector(
  selectUser,
  user => user.getIn(['integrations', 'isClickToDialLoaded']) ?? false
);

export const selectClickToDialContactPropertyDetails = createSelector(
  selectUser,
  user => user.getIn(['integrations', 'contactPropertyDetails']) ?? null
);

export const selectClickToDialData = createSelector(
  selectUser,
  (user) => user.getIn(['integrations', 'clickToDialData']) ?? null
);
export const selectCallCompliance = createSelector(
  selectUser,
  user => user.get('callCompliance')
);

export const selectCallDNC = createSelector(
  selectCallCompliance,
  flags => flags?.get('calldnc')
);

export const selectCallLitigator = createSelector(
  selectCallCompliance,
  flags => flags?.get('calllitigator')
);

export const selectCallDNCLitigator = createSelector(
  selectCallCompliance,
  flags => flags?.get('calldnclitigator')
);

export const selectCallComplianceType = createSelector(
  selectCallCompliance,
  (flags) => {
    if (flags?.get('calldnclitigator')) return 'DNC_LITIGATOR';
    if (flags?.get('calldnc')) return 'DNC';
    if (flags?.get('calllitigator')) return 'LITIGATOR';
    return null;
  }
);

export const selectIsLeadAutomatorUpgradeOpen = createSelector(
  selectUser,
  user => user.getIn(['integrations', 'isLeadAutomatorUpgradeOpen']) ?? false
);

// data/user/selectors.js
export const selectIsComplianceModalOpen = createSelector(
  selectCallCompliance,
  (flags) =>
    Boolean(
      flags?.get('calldnc') ||
      flags?.get('calllitigator') ||
      flags?.get('calldnclitigator')
    )
);
